import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";

const EMPTY = { title: "", employee_name: "", project_or_client: "", year: "", category: "", description: "" };

export default function ResearchFileFormDialog({ open, onOpenChange, file: existingFile, onCreated }) {
  const [form, setForm] = useState(EMPTY);
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const isEdit = !!existingFile;

  useEffect(() => {
    if (open) {
      setForm(
        existingFile
          ? {
              title: existingFile.title, employee_name: existingFile.employee_name,
              project_or_client: existingFile.project_or_client || "", year: existingFile.year || "",
              category: existingFile.category || "", description: existingFile.description || "",
            }
          : EMPTY
      );
      setFile(null);
    }
  }, [open, existingFile]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleSubmit = async () => {
    if (!form.title || !form.employee_name || (!isEdit && !file)) {
      toast.error("Title, employee name and file are required");
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v || ""));
      if (file) fd.append("file", file);
      if (isEdit) {
        await api.put(`/research/${existingFile.id}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        toast.success("Research file updated");
      } else {
        await api.post("/research", fd, { headers: { "Content-Type": "multipart/form-data" } });
        toast.success("Research file added to archive");
      }
      onOpenChange(false);
      onCreated?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="research-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{isEdit ? "Edit Research File" : "Add Research File"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2 max-h-[60vh] overflow-y-auto pr-1">
          <div className="space-y-1.5">
            <Label>Title</Label>
            <Input data-testid="research-form-title-input" value={form.title} onChange={set("title")} placeholder="e.g. Carbon Footprint Study - Textile Sector" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Employee Name (Author)</Label>
              <Input data-testid="research-form-employee-input" value={form.employee_name} onChange={set("employee_name")} placeholder="Who conducted this research?" />
            </div>
            <div className="space-y-1.5">
              <Label>Year</Label>
              <Input data-testid="research-form-year-input" value={form.year} onChange={set("year")} placeholder="e.g. 2025" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Project / Client</Label>
              <Input data-testid="research-form-project-input" value={form.project_or_client} onChange={set("project_or_client")} placeholder="Related project or client" />
            </div>
            <div className="space-y-1.5">
              <Label>Category</Label>
              <Input data-testid="research-form-category-input" value={form.category} onChange={set("category")} placeholder="e.g. Water Studies" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Description</Label>
            <Textarea data-testid="research-form-description-input" value={form.description} onChange={set("description")} rows={2} />
          </div>
          <div className="space-y-1.5">
            <Label>{isEdit && existingFile?.original_filename ? `Replace File (current: ${existingFile.original_filename})` : "File"}</Label>
            <Input data-testid="research-form-file-input" type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="research-form-cancel-button">Cancel</Button>
          <Button onClick={handleSubmit} disabled={submitting} data-testid="research-form-submit-button" className="bg-[#76C121] hover:bg-[#5C9819] text-white">
            {submitting ? "Saving..." : isEdit ? "Save Changes" : "Add to Archive"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
